import { Message } from '@arco-design/web-vue'
import type { AxiosResponse } from 'axios'
import { COOKIE_LOGIN_KEY, LOGIN_FAILED_RETRY_COUNT } from '@/constant/cache-key'
import { loadSessionStorage, removeSessionStorage, saveSessionStorage } from '@/utils/cache'
import { i18n } from '@/plugins/i18n'
import type { RbResponse } from '@/types/misc/http'
import store from '@/store'
import { getAuthFailureFallbackUrl, getLoginEntryUrl, isLocalDebugDomain, resolveAuthRedirectUrl, saveLoginCallbackUrl } from '@/utils/auth-redirect'

interface ErrorResponseData extends Partial<RbResponse<unknown>> {
  return_msg?: string
}

function getFallbackErrorMessage(status: number) {
  switch (status) {
    case 401:
      return i18n.global.t('common.request.unauthorized')
    case 403:
      return i18n.global.t('common.request.forbidden')
    case 404:
      return i18n.global.t('common.request.notFound')
    default:
      return i18n.global.t('common.request.defaultError')
  }
}

async function normalizeErrorResponseData(data: unknown): Promise<ErrorResponseData | null> {
  if (!data) {
    return null
  }

  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    const rawText = (await data.text()).trim()
    if (!rawText) {
      return null
    }

    try {
      return JSON.parse(rawText) as ErrorResponseData
    }
    catch {
      return {
        msg: rawText,
      }
    }
  }

  if (typeof data === 'string') {
    const rawText = data.trim()
    if (!rawText) {
      return null
    }

    try {
      return JSON.parse(rawText) as ErrorResponseData
    }
    catch {
      return {
        msg: rawText,
      }
    }
  }

  if (typeof data === 'object') {
    return data as ErrorResponseData
  }

  return null
}

export async function resolveErrorMessage(err: Pick<AxiosResponse<unknown>, 'status' | 'data'>) {
  const fallbackMessage = getFallbackErrorMessage(err.status)
  const normalizedData = await normalizeErrorResponseData(err.data)
  return normalizedData?.return_msg || normalizedData?.msg || fallbackMessage
}

export async function throwErrorMessage(err: AxiosResponse<ErrorResponseData>) {
  const message = await resolveErrorMessage(err)

  if (err.status === 401) {
    Message.warning(message)
  }
  else {
    Message.error(message)
  }

  return Promise.reject(message)
}

export function clearCookies(preserveKeys: string[] = []) {
  return new Promise((resolve) => {
    const cookies = document.cookie.split(';')
    cookies.forEach((cookie) => {
      const [rawKey] = cookie.split('=')
      const key = rawKey?.trim()
      if (!key || preserveKeys.includes(key)) {
        return
      }
      document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
    })
    resolve(true)
  })
}

export async function handleTokenExpired(err: AxiosResponse<RbResponse<unknown>>) {
  const authStore = store.authStore
  // 未登录，且未模拟登录，则跳转到登录页面
  // if (
  //   err.data && err.data.content
  //   && err.data?.return_msg === '401 Unauthorized'
  //   && isLogin()
  //   && !getSimulated()
  // ) {
  //   const service = err.data.content.slice(err.data.content.indexOf('=') + 1)
  //   saveStorage('service', unescape(service))
  //   await authStore.renewalRoles() // 请求续期
  //   window.location.reload() // 续期后自动刷新
  //   return
  // }

  const loginFailedRetryCount = loadSessionStorage(LOGIN_FAILED_RETRY_COUNT, 0) || 0
  const isStpPermissionDenied = (err.data as { data?: { error_code?: number } } | undefined)?.data?.error_code === 5001
  const authRedirectUrl = resolveAuthRedirectUrl(err.data?.content)

  const message = i18n.global.t('common.request.unauthorized')
  Message.warning(message)
  authStore.logoutCallBack()
  // 保存当前页面的路径，登录后返回
  saveLoginCallbackUrl()

  // 本地开发时不进行跳转登录页
  if (isLocalDebugDomain()) {
    return
  }

  if (isStpPermissionDenied) {
    removeSessionStorage(LOGIN_FAILED_RETRY_COUNT)
    await clearCookies([COOKIE_LOGIN_KEY])
    window.location.replace(getAuthFailureFallbackUrl('no-permission'))
    return
  }

  if (loginFailedRetryCount >= 3) {
    Message.warning(i18n.global.t('common.request.maxTry'))
    removeSessionStorage(LOGIN_FAILED_RETRY_COUNT)
    await clearCookies([COOKIE_LOGIN_KEY])
    window.location.replace(getAuthFailureFallbackUrl('auth-failed'))
    return
  }

  if (authRedirectUrl) {
    // 跳转到登录页面 并带上当前的语言配置
    window.location.replace(authRedirectUrl)
  }
  else {
    // 后端未返回登录页地址时，走统一的 CAS 登录入口，而不是回到 STP 首页。
    saveSessionStorage(LOGIN_FAILED_RETRY_COUNT, loginFailedRetryCount + 1)
    // 保留登录回跳 cookie，避免清理会话时把回跳地址一起删掉。
    await clearCookies([COOKIE_LOGIN_KEY])
    window.location.replace(getLoginEntryUrl())
  }
}
